import { ComponentProps } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { QuoteDocument } from 'modules/finance/components/Quotes/utils'
import { contactFullName } from 'modules/finance/lib/contactsUtils'
import { formatDate } from 'modules/finance/lib/salesInvoiceUtils'

interface SummaryProps {
  quote: QuoteDocument
  showQuoteNumber?: boolean
}

const Summary = ({ quote, showQuoteNumber = false }: SummaryProps) => {
  const { navigation } = useNavigation()

  const detailsLeft = useUnsafeMemo(() => {
    const generateAddress = (): string[] => {
      const address = []

      const line1 = quote.recipientAddressLine1 || quote.client?.address?.line1
      const line2 = quote.recipientAddressLine2 || quote.client?.address?.line2
      const city = quote.recipientCity || quote.client?.address?.city
      const postalCode = quote.recipientPostalCode || quote.client?.address?.postalCode
      const state = quote.recipientState || quote.client?.address?.state
      const countryCode = quote.recipientCountryCode || quote.client?.address?.countryCode

      if (line1) {
        address.push(`${line1},`)
      }

      if (line2) {
        address.push(`${line2},`)
      }

      if (city) {
        address.push(`${city},`)
      }

      if (postalCode) {
        address.push(`${postalCode},`)
      }

      if (state) {
        address.push(`${state},`)
      }

      if (countryCode) {
        address.push(`${countryCode}`)
      }

      return address.filter((item) => !!item)
    }

    const items: ComponentProps<typeof DetailsItemsList>['details'] = []
    items.push({
      title: i18n.t('finance.sale_invoices.detail.issuer'),
      content: {
        type: 'item',
        text: quote.issuerName || quote.legalEntity?.legalName || '-',
      },
    })

    items.push({
      title: i18n.t('finance.sale_invoices.detail.header.billed_to'),
      content: {
        type: 'item',
        text: contactFullName(quote.client),
        action: quote.client?.id
          ? {
              type: 'navigate',
              href: navigation.finance.sales.clients.contactId.fullPath({
                contactId: String(quote.client.id),
              }),
            }
          : undefined,
      },
    })

    items.push({
      title: i18n.t('finance.sale_invoices.detail.billing_details'),
      content: {
        type: 'item',
        text: generateAddress().join(' ') || '-',
      },
    })

    items.push({
      title: i18n.t('finance.sale_invoices.detail.vat'),
      content: {
        type: 'item',
        text: quote.recipientTaxId || quote.client?.taxId || '-',
      },
    })

    return items
  }, [quote])

  const detailsRight = useUnsafeMemo(() => {
    const items: ComponentProps<typeof DetailsItemsList>['details'] = []
    if (showQuoteNumber) {
      items.push({
        title: i18n.t('finance.quotes.form.quote_number'),
        content: {
          type: 'item',
          text: quote.quoteNumber || '-',
        },
      })
    }

    items.push({
      title: i18n.t('finance.currency'),
      content: {
        type: 'item',
        text: quote.currency || '-',
      },
    })

    items.push({
      title: i18n.t('finance.quotes.form.expiration_date'),
      content: {
        type: 'item',
        text: formatDate(quote.expirationDate),
      },
    })

    if (quote.deal) {
      items.push({
        title: i18n.t('finance.quotes.form.deal'),
        content: {
          type: 'item',
          text: quote.deal?.name || '-',
          action: {
            type: 'navigate',
            href: navigation.finance.sales.deals.dealId.fullPath({ dealId: quote.deal?.id }),
          },
        },
      })
    }

    return items
  }, [
    navigation.finance.sales.deals.dealId,
    quote.currency,
    quote.deal,
    quote.expirationDate,
    quote.quoteNumber,
  ])

  return (
    <Box flexDirection="row">
      <Box width="full">
        <DetailsItemsList title={''} details={detailsLeft} />
      </Box>
      <Box width="full">
        <DetailsItemsList title={''} details={detailsRight} />
      </Box>
    </Box>
  )
}

export default Summary
